<template>
  <v-card
    :id="`trajectory-${trajectory.id}`"
    variant="outlined"
    class="mb-3 dual-servo-card"
    :class="{ 'trajectory-active': trajectory.id === currentTrajectoryId }"
  >
    <div class="pl-4 pr-4">
      <div class="d-flex align-center">
      <span class="text-subtitle-1 text-high-emphasis font-weight-light">
        {{ trajectory.name }} - 轨迹参数与角度参数
      </span>
      <v-spacer></v-spacer>
      <v-col cols="2" style="max-width:120px;">
        <v-text-field
          :model-value="trajectory.servos && trajectory.servos.length > 0 ? trajectory.servos[0].l : 1"
          label="循环次数"
          type="number"
          min="1"
          density="compact"
          variant="outlined"
          hide-details
          @update:model-value="updateTrajectoryLoopCount(trajectory, $event)"
        />
      </v-col>
      <v-btn prepend-icon="mdi-delete" class="ml-3 me-2 text-none" color="red-lighten-2"
             variant="outlined"
             @click="openDeleteTrajectoryDialog(trajectory.id, trajectory.name)">删除当前配置
      </v-btn>
      </div>
      <div v-if="trajectory.servos?.length" class="text-caption text-medium-emphasis mt-2">
        <div v-for="(line, si) in trajectoryServoBindingLines(trajectory)" :key="si">{{ line }}</div>
      </div>
    </div>
    <v-divider></v-divider>
    <v-card-text>
      <!-- 轨迹参数部分 -->
      <div class="mb-4">
        <div class="text-subtitle-2 font-weight-medium mb-2">轨迹参数</div>
        <div style="font-weight:500;" class="pb-2">操作片段：</div>
        <div v-for="(point, pointIdx) in (trajectory.trajectoryPoints || [{x: trajectory.trajectoryX, y: trajectory.trajectoryY}])" :key="pointIdx" class="d-flex align-center mb-2">
          <span class="mr-2">步数:</span>
          <v-text-field
            :model-value="getTrajectoryPointSteps(trajectory, pointIdx)"
            type="number"
            density="compact"
            variant="outlined"
            hide-details
            style="width: 80px;"
            @update:model-value="updateTrajectoryPointSteps(trajectory, pointIdx, $event)"
          />
          <span class="mx-2">X坐标:</span>
          <v-text-field
            v-model.number="point.x"
            type="number"
            density="compact"
            variant="outlined"
            hide-details
            style="width: 120px;"
            @click="moveArmToPosition(trajectory.id, pointIdx)"
            @update:model-value="updateTrajectoryPointForTrajectory(trajectory, pointIdx)"
          />
          <span class="mx-2">Y坐标:</span>
          <v-text-field
            v-model.number="point.y"
            type="number"
            density="compact"
            variant="outlined"
            hide-details
            style="width: 120px;"
            @click="moveArmToPosition(trajectory.id, pointIdx)"
            @update:model-value="updateTrajectoryPointForTrajectory(trajectory, pointIdx)"
          />
          <v-btn
            size="small"
            color="primary"
            variant="outlined"
            class="mx-1"
            @click="setCurrentPointForTrajectory(trajectory.id, pointIdx)"
          >
            设置当前参数
          </v-btn>
          <v-btn prepend-icon="mdi-delete" color="red" variant="text" @click="removeTrajectoryPoint(trajectory.id, pointIdx)"/>
        </div>
        <div class="mb-3">
          <v-btn size="small" color="primary" variant="outlined" @click="addTrajectoryPoint(trajectory.id)" class="mr-2">添加片段</v-btn>
          <v-btn size="small" color="secondary" variant="outlined" prepend-icon="mdi-mirror" @click="openMirrorTrajectoryDialog(trajectory.id)" class="mr-2">镜像轨迹</v-btn>
          <v-btn size="small" color="orange" variant="outlined" prepend-icon="mdi-refresh" @click="openAddReturnTripDialog(trajectory.id)">添加返程</v-btn>
        </div>
        <div class="text-body-2 mt-2">
          <span v-if="trajectory.servos && trajectory.servos.length > 0">
            X: {{ safeToFixed(trajectory.id === currentTrajectoryId && showDualArmVisualization ? trajectoryX : (trajectory.trajectoryPoints && trajectory.trajectoryPoints.length > 0 ? trajectory.trajectoryPoints[0].x : trajectory.trajectoryX)) }} |
            Y: {{ safeToFixed(trajectory.id === currentTrajectoryId && showDualArmVisualization ? trajectoryY : (trajectory.trajectoryPoints && trajectory.trajectoryPoints.length > 0 ? trajectory.trajectoryPoints[0].y : trajectory.trajectoryY)) }} |
            A臂（左支座）{{ getServoALabel(trajectory) }}: {{ safeToFixed(trajectory.id === currentTrajectoryId && showDualArmVisualization ? dualArmConfig.servoA - (trajectory.zeroPositionAngles?.servoA || 0) : trajectory.armConfig.servoA - (trajectory.zeroPositionAngles?.servoA || 0)) }}° |
            B臂（右支座）{{ getServoBLabel(trajectory) }}: {{ safeToFixed(trajectory.id === currentTrajectoryId && showDualArmVisualization ? dualArmConfig.servoB - (trajectory.zeroPositionAngles?.servoB || 0) : trajectory.armConfig.servoB - (trajectory.zeroPositionAngles?.servoB || 0)) }}°
          </span>
        </div>
        <div class="text-body-2 mt-2">
          臂长: L1={{ trajectory.config.armLengths?.L1 || 0 }}, L2={{ trajectory.config.armLengths?.L2 || 0 }}, L3={{ trajectory.config.armLengths?.L3 || 0 }}, L4={{ trajectory.config.armLengths?.L4 || 0 }} |
          间距: {{ trajectory.config.servoDistance || 0 }} |
          零位坐标: ({{ getOriginPosition(trajectory.config, 'origin').x }},{{ getOriginPosition(trajectory.config, 'origin').y }})
        </div>
        <div class="mt-3">
          <v-btn
            size="small"
            color="primary"
            variant="outlined"
            prepend-icon="mdi-refresh"
            @click="resetToInitialPositionForTrajectory(trajectory)"
          >
            回到零位
          </v-btn>
          <v-btn
            size="small"
            color="success"
            variant="outlined"
            prepend-icon="mdi-eye"
            class="ml-2"
            @click="viewTrajectoryArmVisualization(trajectory.id)"
          >
            查看当前机械臂
          </v-btn>
          <v-btn
            size="small"
            color="info"
            variant="outlined"
            :prepend-icon="showAngleParams ? 'mdi-eye-off' : 'mdi-eye'"
            class="ml-2"
            @click="showAngleParams = !showAngleParams"
          >
            {{ showAngleParams ? '隐藏角度参数' : '显示角度参数' }}
          </v-btn>
        </div>
      </div>

      <v-divider class="my-3"></v-divider>

      <!-- 角度参数部分 -->
      <div v-show="showAngleParams">
        <div class="text-subtitle-2 font-weight-medium mb-2">角度参数</div>
        <div v-for="(servo, idx) in trajectory.servos" :key="idx">
          <div class="pl-4 pr-4 d-flex align-center mb-2">
            <span class="text-subtitle-2 font-weight-medium">
              舵机{{ servo.s }}「{{ servo.name }}」· {{ physicalServoArmCaption(trajectory, idx) }}
              <span style="font-size:14px;color:#888;">（运动范围：{{ servo.range ? servo.range[0] : 0 }} 到 {{ servo.range ? servo.range[1] : 0 }}）</span>
            </span>
            <v-spacer></v-spacer>
            <v-col cols="2" style="max-width:120px;">
              <v-text-field v-model.number="servo.l" label="循环次数" type="number" min="1" density="compact" variant="outlined" hide-details disabled/>
            </v-col>
          </div>
          <div style="font-weight:500;" class="pb-2">操作片段：</div>
          <template v-for="(motion, mIdx) in servo.m" :key="mIdx">
            <div v-if="motion && motion.steps !== undefined" class="d-flex align-center mb-2">
              <v-text-field v-model.number="motion.steps" label="步(20ms/步)" type="number" :min="servo.range?servo.range[0]:-200" :max="servo.range?servo.range[1]:200" style="width:120px;" density="compact" variant="outlined" disabled/>
              <span class="mx-2">参数：</span>
              <v-text-field v-model.number="motion.param[0]" label="开始角度" type="number" :min="servo.range?servo.range[0]:-200" :max="servo.range?servo.range[1]:200" style="width:90px;" density="compact" variant="outlined" class="mx-1" disabled/>
              <v-text-field v-model.number="motion.param[2]" label="1/3点y坐标" type="number" :min="servo.range?servo.range[0]:-200" :max="servo.range?servo.range[1]:200" style="width:110px;" density="compact" variant="outlined" class="mx-1" disabled/>
              <v-text-field v-model.number="motion.param[3]" label="2/3点y坐标" type="number" :min="servo.range?servo.range[0]:-200" :max="servo.range?servo.range[1]:200" style="width:110px;" density="compact" variant="outlined" class="mx-1" disabled/>
              <v-text-field v-model.number="motion.param[1]" label="结束角度" type="number" :min="servo.range?servo.range[0]:-200" :max="servo.range?servo.range[1]:200" style="width:90px;" density="compact" variant="outlined" class="mx-1" disabled/>
              <v-btn
                size="small"
                color="primary"
                variant="outlined"
                class="mx-1"
                @click="setCurrentAngleForTrajectory(trajectory.id, idx, mIdx)"
              >
                设置当前参数
              </v-btn>
              <v-btn prepend-icon="mdi-delete" color="red" variant="text" @click="removeMotionForTrajectory(trajectory.id, idx, mIdx)"/>
            </div>
          </template>
          <v-btn size="small" color="primary" variant="outlined" @click="addMotionForTrajectory(trajectory.id, idx)" class="mb-3">添加片段</v-btn>
          <v-divider v-if="idx < trajectory.servos.length - 1" class="my-3"></v-divider>
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script setup>
import { inject } from 'vue'
import {
  getServoALabel,
  getServoBLabel,
  trajectoryServoBindingLines,
  physicalServoArmCaption
} from '../dualArmTrajectory.js'
import { safeToFixed } from '../utils/format.js'
import { getOriginPosition } from '../utils/trajectoryConfig.js'

defineProps({
  trajectory: { type: Object, required: true }
})

// 由 App.vue provide 的轨迹管理器（含镜像轨迹/添加返程入口）
const {
  currentTrajectoryId,
  dualArmConfig,
  trajectoryX,
  trajectoryY,
  showDualArmVisualization,
  showAngleParams,
  updateTrajectoryLoopCount,
  openDeleteTrajectoryDialog,
  getTrajectoryPointSteps,
  updateTrajectoryPointSteps,
  moveArmToPosition,
  updateTrajectoryPointForTrajectory,
  setCurrentPointForTrajectory,
  removeTrajectoryPoint,
  addTrajectoryPoint,
  resetToInitialPositionForTrajectory,
  viewTrajectoryArmVisualization,
  setCurrentAngleForTrajectory,
  removeMotionForTrajectory,
  addMotionForTrajectory,
  openMirrorTrajectoryDialog,
  openAddReturnTripDialog
} = inject('trajectoryManager')
</script>
